import { ClipboardEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { components, InputActionMeta, InputProps, MultiValue } from 'react-select';
import UserSelector from '../../components/shared/UserSelector/UserSelector';
import { ServiceUser } from '../../services/service-user';
import { EvaluationLimits } from '../../util/evaluationContract';
import { reportUnexpected } from '../page-supplier-evaluations/reportUnexpected';
import {
  RecipientOption,
  isOwnRecipientOption,
  mergeRecipients,
  recipientOptionFor,
  splitPastedEmails,
  splitTypedEmails,
  userRecipientOptions,
} from './evaluationStart';
import styles from './app-evaluation-start.module.scss';

export type ExistingRecipient = { userId?: string | null; email?: string | null };

export type NoEmailRecipient = ExistingRecipient & { name?: string | null };

export type RecipientFieldProps = {
  value: RecipientOption[];
  onChange: (next: RecipientOption[]) => void;
  max?: number;
  existing?: ExistingRecipient[];
  // The utvärdering's sender: the backend sends no email for their own row.
  noEmailFor?: NoEmailRecipient | null;
};

type UserGroups = Parameters<typeof userRecipientOptions>[0];

const NO_EXISTING: ExistingRecipient[] = [];

const matchesAny = (option: RecipientOption, people: ExistingRecipient[]) => {
  const email = (option.email ?? '').toLowerCase();
  return people.some(
    entry =>
      (entry.userId && entry.userId === option._id) ||
      (email && (entry.email ?? '').toLowerCase() === email),
  );
};

const canAddTyped = (input: string) => input.includes('@');

const isCreatedFromInput = (option: RecipientOption) => Boolean(option.__isNew__ && !option.email);

const searchText = (option: RecipientOption) => `${option.label} ${option.email ?? ''}`;

const RecipientField = ({
  value,
  onChange,
  max = EvaluationLimits.MaxRecipientsPerRequest,
  existing = NO_EXISTING,
  noEmailFor = null,
}: RecipientFieldProps) => {
  const [t] = useTranslation('evaluations');
  const [groups, setGroups] = useState<UserGroups>([]);
  const [usersFailed, setUsersFailed] = useState(false);
  const [invalid, setInvalid] = useState<string[]>([]);
  const [alreadyAdded, setAlreadyAdded] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    let mounted = true;
    ServiceUser.getUsersWithRoles()
      .then((loaded: UserGroups | null) => {
        if (mounted) setGroups(loaded ?? []);
      })
      .catch(error => {
        reportUnexpected(error);
        if (mounted) setUsersFailed(true);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const users = useMemo(() => userRecipientOptions(groups, t), [groups, t]);
  const options = useMemo(
    () => users.filter(option => !matchesAny(option, existing)),
    [users, existing],
  );
  const shownValue = value.map(
    picked => (picked._id && users.find(option => option._id === picked._id)) || picked,
  );

  const noEmailRow = noEmailFor
    ? value.find(option => matchesAny(option, [noEmailFor]))
    : undefined;

  const commit = (current: readonly RecipientOption[], typed: string[]) => {
    const isAlreadyAdded = (token: string) =>
      matchesAny(recipientOptionFor(token.trim(), users), existing);
    const merged = mergeRecipients(
      current,
      typed.filter(token => !isAlreadyAdded(token)),
      users,
      max,
    );
    setInvalid(merged.invalid);
    setAlreadyAdded(Array.from(new Set(typed.filter(isAlreadyAdded).map(token => token.trim()))));
    onChange(merged.next);
  };

  const handleChange = (picked: MultiValue<RecipientOption>) => {
    const created = picked.filter(isCreatedFromInput);
    const kept = picked.filter(option => !isCreatedFromInput(option));
    commit(
      kept,
      created.flatMap(option => splitPastedEmails(option.value)),
    );
  };

  const nextInput = (text: string, meta: InputActionMeta) => {
    if (meta.action === 'input-change') {
      const { complete, rest } = splitTypedEmails(text);
      if (!complete.length) return text;
      commit(value, complete);
      return rest;
    }
    if (meta.action === 'input-blur' && meta.prevInputValue.includes('@')) {
      commit(value, splitPastedEmails(meta.prevInputValue));
    }
    return text;
  };

  const handleInputChange = (text: string, meta: InputActionMeta) => {
    setInputValue(nextInput(text, meta));
  };

  // A colleague whose address contains the typed one stays focused, so Enter adds what was typed.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing || !canAddTyped(inputValue)) return;
    event.preventDefault();
    commit(value, splitPastedEmails(inputValue));
    setInputValue('');
  };

  const handlePaste = useRef<(text: string) => boolean>(() => false);
  handlePaste.current = text => {
    if (!text.includes('@') || !/[\s,;]/.test(text.trim())) return false;
    commit(value, splitPastedEmails(text));
    return true;
  };

  const selectComponents = useMemo(
    () => ({
      Input: (props: InputProps<RecipientOption, true>) => (
        <components.Input
          {...props}
          onPaste={(event: ClipboardEvent<HTMLInputElement>) => {
            if (handlePaste.current(event.clipboardData.getData('text'))) event.preventDefault();
          }}
        />
      ),
    }),
    [],
  );

  return (
    <div className={styles.recipientField}>
      <UserSelector<RecipientOption, true>
        isMulti
        isCreatable
        isOverflow
        isSearchable
        isDisabled={max <= 0}
        label={t('recipients.label', 'Mottagare')}
        placeholder={t('recipients.placeholder', 'Välj personer eller skriv e-postadresser')}
        options={options}
        value={shownValue}
        onChange={handleChange}
        onInputChange={handleInputChange}
        getSearchString={searchText}
        components={selectComponents}
        formatCreateLabel={input =>
          t('recipients.addEmail', 'Lägg till {{email}}', { email: input })
        }
        {...({ isValidNewOption: canAddTyped, inputValue, onKeyDown: handleKeyDown } as object)}
      />
      {usersFailed && (
        <p className={styles.hint}>
          {t('recipients.loadFailed', 'Kollegor kunde inte hämtas. Du kan skriva e-postadresser.')}
        </p>
      )}
      {invalid.length > 0 && (
        <p className={`${styles.hint} ${styles.invalid}`}>
          {t('recipients.invalidAddress', {
            count: invalid.length,
            emails: invalid.join(', '),
            defaultValue_one: 'Ogiltig adress: {{emails}}. Kontrollera stavningen.',
            defaultValue_other: 'Ogiltiga adresser: {{emails}}. Kontrollera stavningen.',
          })}
        </p>
      )}
      {alreadyAdded.length > 0 && (
        <p className={`${styles.hint} ${styles.invalid}`}>
          {t('recipients.alreadyRecipient', 'Redan mottagare: {{emails}}', {
            emails: alreadyAdded.join(', '),
          })}
        </p>
      )}
      {noEmailRow && (
        <p className={styles.hint}>
          {isOwnRecipientOption(noEmailRow)
            ? t('quick.selfInfo', 'Du får inget mejl. Svara med Svara nu på utvärderingens sida.')
            : t('recipients.senderNoEmail', '{{name}} skickade utvärderingen och får inget mejl.', {
                name: noEmailFor?.name || noEmailFor?.email || noEmailRow.label,
              })}
        </p>
      )}
      {value.length >= max && (
        <p className={styles.hint}>
          {t('recipients.cap', 'Högst {{max}} mottagare per utvärdering', {
            max: EvaluationLimits.MaxRecipientsPerRequest,
          })}
        </p>
      )}
    </div>
  );
};

export default RecipientField;
